import React, { useEffect, useState, useRef } from "react";
import { Badge, Button, Flex, Layout, Menu, Modal, Popover } from "antd";
import { MenuOutlined, LeftOutlined, RightOutlined, BugOutlined } from "@ant-design/icons";
import { useBugReporter } from "../Bugreporter";
import { withRouter } from "react-router-dom";
import { push } from "react-router-redux";
import { connect } from "react-redux";

import { controller } from "../controller";
import "./app.local.css";

// Icons
import dashboard from "../assets/icons/dashboardIcon.png";
import logoSmall from "../assets/img/logo-collapsed.png";
import target from "../assets/icons/target-icon.svg";
import treatmentPlan from "../assets/icons/data.png";
import setting from "../assets/icons/ticket20.png";
import payment from "../assets/icons/payment.png";
import office from "../assets/icons/office.png";
import ticket from "../assets/icons/ticket.svg";
import insuranceRequests from "../assets/icons/insurance-requests.svg";
import { sidebarAccess, planGated } from "./sidebarAccess";
import { openPlanModal } from "./Subscription/planModal";
import { useOfficeFeatures } from "./officeFeatures";

const { SubMenu, ItemGroup } = Menu;
const { Sider } = Layout;

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

// --- Sidebar highlighting ----------------------------------------------------
//
// Which item is selected, and which submenu is open, for the current route.
//
// This replaced a 38-branch nested ternary (plus a parallel if/else chain for the
// submenus) that tested `window.location.href.includes(<substring>)` and took the
// first hit. Substring-matching a URL is not a way to identify a route, and it
// was actively wrong on four pages:
//
//   /office/carriers                -> "ar" matched inside "carriers"
//   /admin/insurance-carrier-calls  -> "ar" matched inside "carrier"
//                                      ...both highlighted Accounts Receivable.
//   /insurance-requests             -> "insurance" matched first, highlighting
//                                      the "Predetermination and Post Payment"
//                                      beta item under Admin Access.
//   /office/portal-credentials      -> matched nothing at all and fell through
//                                      to the Payment Requests default.
//
// Matching below is exact-or-child on the hash path, LONGEST ROUTE FIRST, so a
// route that is a prefix of another (/plans vs /plans-management,
// /treatment-plans vs /treatment-plans-account-receivable) can never shadow it
// no matter what order these entries are written in.
//
// `key` MUST equal the `key` of the matching <Menu.Item>, and `open` the `key` of
// its enclosing <SubMenu> (empty for a top-level item).
const MENU_ROUTES = [
  // Top-level items
  { route: "/treatment-plans", key: "11", open: [] },
  { route: "/ftc-tasks", key: "ftc-tasks", open: ["ftcPanel"] },
  { route: "/ftc-inbox", key: "ftc-inbox", open: ["ftcPanel"] },
  { route: "/ftc-reports", key: "ftc-reports", open: ["ftcPanel"] },
  { route: "/insurance-requests", key: "insurance-requests", open: [] },
  { route: "/patient-board", key: "10", open: [] },
  { route: "/targets-and-rewards", key: "17", open: [] },
  // Reachable only from the footer Popover (a separate `selectable={false}`
  // menu) since the Settings submenu was commented out, so nothing highlights
  // for these today. Kept for the same reason as the Membership block below.
  { route: "/offices", key: "132", open: [] },
  { route: "/add-connected-account", key: "4", open: [] },

  // Payments & AR (sub2)
  { route: "/payment-requests", key: "1", open: ["sub2"] },
  { route: "/ar", key: "9", open: ["sub2"] },
  { route: "/ar-detail", key: "9", open: ["sub2"] },
  { route: "/transactions", key: "141", open: ["sub2"] },
  { route: "/failed-payments", key: "142", open: ["sub2"] },

  // My Office (sub5)
  { route: "/office/profile", key: "8", open: ["sub5"] },
  { route: "/office/providers", key: "16", open: ["sub5"] },
  // /office/carriers redirects to Carrier Knowledge (Admin Access), which
  // highlights its own item — no mapping needed here any more.
  { route: "/office/portal-credentials", key: "79", open: ["sub5"] },
  { route: "/office/appointments", key: "15", open: ["sub5"] },
  { route: "/office/review", key: "12", open: ["sub5"] },
  { route: "/Services-management", key: "73", open: ["sub5"] },
  { route: "/admin/insurance-carrier-calls", key: "78", open: ["sub5"] },

  // Membership (sub1) — the whole submenu is COMMENTED OUT in the JSX below, so
  // none of these items currently render. The mappings are kept (and correct) so
  // that re-enabling the submenu restores highlighting with no extra work; until
  // then they simply select a key that isn't on screen, which is a no-op.
  { route: "/members", key: "60", open: ["sub1"] },
  { route: "/plans", key: "70", open: ["sub1"] },
  { route: "/plan-detail", key: "72", open: ["sub1"] },
  { route: "/plans-management", key: "72", open: ["sub1"] },
  { route: "/visit", key: "80", open: ["sub1"] },

  // Admin Access (adminDash)
  { route: "/crm", key: "crm", open: ["adminDash"] },
  { route: "/crm-analytics", key: "crm-analytics", open: ["adminDash"] },
  { route: "/crm-settings", key: "crm-settings", open: ["adminDash"] },
  // Requests merged into Carrier Knowledge; the old route redirects there, so
  // it highlights that item rather than one that no longer exists.
  { route: "/crm-requests", key: "carrier-knowledge", open: ["adminDash"] },
  { route: "/ftc-users", key: "ftc-users", open: ["adminDash"] },
  { route: "/carrier-knowledge", key: "carrier-knowledge", open: ["adminDash"] },
  { route: "/analytics", key: "analytics", open: ["adminDash"] },
  { route: "/admin/predetermination-rules", key: "predet-rules", open: ["adminDash"] },
  { route: "/appointments", key: "6", open: ["adminDash"] },
  { route: "/add-provider", key: "3", open: ["adminDash"] },
  { route: "/provider-scheduling", key: "5", open: ["adminDash"] },
  { route: "/discount-options", key: "143", open: ["adminDash"] },
  { route: "/data/health-categories", key: "30", open: ["adminDash"] },
  { route: "/data/procedures", key: "31", open: ["adminDash"] },
  { route: "/campaigns", key: "campaigns", open: ["adminDash"] },
  { route: "/insurance", key: "144", open: ["adminDash"] },
  { route: "/insurance-breakdown", key: "145", open: ["adminDash"] },
  { route: "/dashboard", key: "0", open: ["adminDash"] },
  { route: "/treatment-plans-account-receivable", key: "7", open: ["adminDash"] },
].sort((a, b) => b.route.length - a.route.length);

// Unknown route -> the previous chain's own fallback, so pages with no sidebar
// entry behave exactly as before.
const MENU_FALLBACK = { key: "1", open: ["sub2"] };

function resolveMenuRoute() {
  // Hash routing: location.hash is "#/office/carriers?x=1".
  const path = (window.location.hash || "")
    .replace(/^#/, "")
    .split("?")[0]
    .replace(/\/+$/, "");
  if (!path) return MENU_FALLBACK;
  return (
    MENU_ROUTES.find((r) => path === r.route || path.startsWith(r.route + "/")) ||
    MENU_FALLBACK
  );
}

function Sidebar({ dispatch, reloadSidebar }) {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  const [collapsed, setCollapsed] = useState(() => {
    const savedCollapsed = localStorage.getItem("collapsed");
    if (savedCollapsed !== null) {
      return JSON.parse(savedCollapsed);
    }
    return window.innerWidth < 991;
  });
  const prevWidthRef = useRef(window.innerWidth);
  const { openBugReporter } = useBugReporter();

  // The guided tour collapses the sidebar for more room while it runs. This is
  // visual only (no localStorage write), so the user's saved preference is
  // untouched; on restore we fall back to that saved preference.
  useEffect(() => {
    const onTourSidebar = (e) => {
      const d = (e && e.detail) || {};
      if (d.restore) {
        const saved = localStorage.getItem("collapsed");
        setCollapsed(saved !== null ? JSON.parse(saved) : window.innerWidth < 991);
      } else {
        setCollapsed(!!d.collapsed);
      }
    };
    window.addEventListener("go-sidebar", onTourSidebar);
    return () => window.removeEventListener("go-sidebar", onTourSidebar);
  }, []);

  const [officeProfile, setOfficeProfile] = useState(
    localStorage.getItem("smilepassLogo") ? JSON.parse(localStorage.getItem("smilepassLogo")).data : [],
  );

  const [isPracticeAdmin, setIsPracticeAdmin] = useState(false);
  const [isFTCPersonnel, setIsFTCPersonnel] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  // Which practice-level tools this user gets; FTC personnel never get the
  // practice's own (user management, billing). isSuperAdmin goes in too because
  // checkPracticeAdmin below raises isFTCPersonnel for super admins (so they can
  // reach the FTC Panel) — see sidebarAccess.js.
  // The selected office's feature switches (CRM office drawer → Features). The
  // office-features response also knows whether the viewer is a super admin, which
  // arrives before checkPracticeAdmin on a cached visit — so either one counts.
  const officeFeatures = useOfficeFeatures();
  const access = sidebarAccess({
    isPracticeAdmin,
    isFTCPersonnel,
    isSuperAdmin: isSuperAdmin || officeFeatures.isSuperAdmin,
    features: officeFeatures.features,
  });
  // Product pages open the Choose-a-plan modal instead of navigating when this
  // office has no plan and no trial left (the backend's gate; never for super
  // admins or FTC personnel). Settings, My Office and admin tools stay reachable.
  const planBlocked = planGated({ ...officeFeatures, isSuperAdmin: isSuperAdmin || officeFeatures.isSuperAdmin });
  const goProduct = (route, before) => {
    if (planBlocked) {
      openPlanModal({ reason: officeFeatures.plan && officeFeatures.plan.reason });
      return;
    }
    if (before) before();
    dispatch(push(route));
  };
  const [displaySidebar, setDisplaySidebar] = useState("none");

  const [default_hover] = useState(() => resolveMenuRoute().key);

  // Open the submenu that owns the current route (see MENU_ROUTES). Collapsed
  // sidebars show no submenus, so they start closed.
  const [openSubMenus, setOpenSubMenus] = useState(() =>
    collapsed ? [] : resolveMenuRoute().open,
  );

  const handleOpenChange = (value) => {
    setOpenSubMenus(value);
  };

  const checkPracticeAdmin = async () => {
    const response = await controller.checkPracticeAdmin();
    if (response.data.result && response.data.result === "super_admin") {
      setIsPracticeAdmin(true);
      setIsFTCPersonnel(true); // super admins can reach every surface
      setIsSuperAdmin(true);
    } else {
      setIsSuperAdmin(false);
      const roles = Array.isArray(response.data.result) ? response.data.result : [];
      setIsFTCPersonnel(roles.some((name) => typeof name === "string" && name.indexOf("FTC-Personnel") !== -1));
      // A real practice owner/admin is anyone who administers >=1 office — they get
      // the Settings -> User Management page (super_admin already handled above).
      try {
        const off = await controller.getAdministeredOffices();
        setIsPracticeAdmin(Array.isArray(off.data) && off.data.length > 0);
      } catch (e) {
        setIsPracticeAdmin(false);
      }
    }
  };

  const [count, setCount] = useState(0);

  const getCounterData = async () => {
    const response = await controller.getBadge();
    if (response.data) setCount(response.data);
  };

  useEffect(() => {
    getCounterData();
  }, [reloadSidebar]);

  useEffect(() => {
    getLogo();
    getCounterData();
    checkPracticeAdmin();

    function handleResize() {
      const currentWidth = window.innerWidth;
      const prevWidth = prevWidthRef.current;

      const crossedToMobile = prevWidth >= 991 && currentWidth < 991;
      const crossedToDesktop = prevWidth < 991 && currentWidth >= 991;

      // ✅ Only override collapsed WHEN breakpoint is actually crossed
      if (crossedToMobile) {
        setCollapsed(true);
        localStorage.setItem("collapsed", true);
      }

      if (crossedToDesktop) {
        setCollapsed(false);
        localStorage.setItem("collapsed", false);
      }

      prevWidthRef.current = currentWidth;
    }

    window.addEventListener("resize", handleResize);
    // Call handleResize initially to set the correct state
    //handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getLogo = async () => {
    const response = await controller.getLogo();
    setOfficeProfile(response.data);
  };

  const onCollapse = (collapsedValue) => {
    setOpenSubMenus([]);
    setCollapsed(collapsedValue);
    localStorage.setItem("collapsed", collapsedValue);
  };

  return (
    <div className="sidebar_container">
      <div className="iconMenuSideBae">
        <MenuOutlined
          onClick={() => {
            setDisplaySidebar("absolute");
          }}
          style={
            windowDimensions
              ? windowDimensions.width > 600
                ? { display: "none" }
                : windowDimensions.width > 750
                  ? { fontSize: "22px", color: "#7A08FA" }
                  : { fontSize: "15px", color: "#7A08FA" }
              : { display: "none" }
          }
        />
      </div>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        trigger={null}
        className="collapsedMenu"
        width={300}
        collapsedWidth={64}
        style={{ position: "fixed", top: 0, bottom: 0, left: 0, zIndex: 100 }}
      >
        <div className="logoMenu p0 sidebar-logo-row">
          {!collapsed ? (
            <img src={officeProfile.dark} alt="smilepass" className="smpassLogo" />
          ) : (
            <img src={logoSmall} alt="smilepass" className="smpassLogoCollpased" />
          )}
        </div>

        <button
          type="button"
          className="sidebar-edge-toggle"
          onClick={() => onCollapse(!collapsed)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <RightOutlined /> : <LeftOutlined />}
        </button>

        {/*Divider*/}
        <div
          style={{
            height: "1px",
            background: "linear-gradient(90deg, rgba(224, 225, 226, 0) 0%, #E0E1E2 49.52%, rgba(224, 225, 226, 0.15625) 99.04%)",
            border: "none",
            marginBottom: "24px",
            width: "100%",
          }}
        />

        <div className="sidebar-scroll-area">
        <Menu
          className="collapsedMenu"
          defaultSelectedKeys={[default_hover]}
          mode="inline"
          openKeys={openSubMenus}
          onOpenChange={handleOpenChange}
          theme="dark"
        >
          {/* Leads the menu because it is also the post-login landing page for
              practice admins (auth/Login.js routeLoggedInUser). Shown to practice
              admins (superuser, or holds an administered office) and to FTC
              personnel, who work this queue for the office; the practice's own
              tools below (user management, billing) are admin-only. Ordering
              here is purely visual: MENU_ROUTES maps route -> key independently,
              so moving this item cannot affect which item highlights. */}
          {access.insuranceVerification && (
            <Menu.Item
              className="sidebar_menuitem"
              key="insurance-requests"
              onClick={() => goProduct("/insurance-requests")}
              title="Insurance Verification"
            >
              <img src={insuranceRequests} alt="" className="Icon" />
              <span style={{ fontSize: 16 }}>{collapsed ? "" : "Insurance Verification"}</span>
            </Menu.Item>
          )}

          {access.treatmentPlanning && (
            <Menu.Item
              className="sidebar_menuitem"
              key="11"
              onClick={() => goProduct("/treatment-plans", () =>
                localStorage.setItem("patient-engagment-sub", "treatment-plans"))}
              title="Treatment Planning"
            >
              <img src={treatmentPlan} alt="" className="Icon" />
              <span style={{ fontSize: 16 }}>{collapsed ? "" : "Treatment Planning"}</span>
            </Menu.Item>
          )}

          {/* Payments SubMenu */}
          {access.paymentsAr && (
          <SubMenu
            key="sub2"
            className="sidebar_submenu"
            title={
              <span className="align-menu">
                <img src={payment} alt="" className="Icon" />
                <span style={{ fontSize: 16 }}>{collapsed ? "" : "Payments & AR"}</span>
              </span>
            }
          >
            <Menu.Item className="sidebar_menuitem" key="9" onClick={() => goProduct("/ar")}>
              <span>Accounts Receivable</span>
            </Menu.Item>

            <Menu.Item className="sidebar_menuitem" key="1" onClick={() => goProduct("/payment-requests")}>
              <span>Online Payments</span>
            </Menu.Item>

            <Menu.Item className="sidebar_menuitem w102p" key="141" onClick={() => goProduct("/transactions")}>
              <span>Processed Payments</span>
              <Badge count={count.t_count ? count.t_count : 0} className="ml10" style={{ backgroundColor: "#9340ff" }} />
            </Menu.Item>

            <Menu.Item
              className="sidebar_menuitem w102p"
              key="142"
              style={{ marginBottom: "0px" }}
              onClick={() => goProduct("/failed-payments")}
            >
              <span>Failed Payments</span>
              <Badge className="ml10" count={count.fp_count ? count.fp_count : 0} style={{ backgroundColor: "red" }} />
            </Menu.Item>
          </SubMenu>
          )}

          {access.targetsRewards && (
            <Menu.Item className="sidebar_menuitem" key="17" onClick={() => goProduct("/targets-and-rewards")} title="Targets and Rewards">
              <img src={target} alt="" className="Icon" />
              <span style={{ fontSize: 16 }}>{collapsed ? "" : "Targets and Rewards"}</span>
            </Menu.Item>
          )}

          {/* FTC Panel — everything an FTC does day to day. Tasks was a lone
              top-level item until correspondence arrived; a second sibling is the
              point at which a parent menu earns its place. */}
          {isFTCPersonnel && (
            <SubMenu
              key="ftcPanel"
              className="sidebar_submenu"
              title={
                <span className="align-menu">
                  <img src={ticket} alt="" className="Icon" />
                  <span style={{ fontSize: 16 }}>{collapsed ? "" : "FTC Panel"}</span>
                </span>
              }
            >
              <Menu.Item className="sidebar_menuitem" key="ftc-tasks" onClick={() => dispatch(push("/ftc-tasks"))} title="FTC Tasks">
                <span style={{ fontSize: 15 }}>{collapsed ? "" : "FTC Tasks"}</span>
              </Menu.Item>
              <Menu.Item className="sidebar_menuitem" key="ftc-inbox" onClick={() => dispatch(push("/ftc-inbox"))} title="Inbox">
                <span style={{ fontSize: 15 }}>{collapsed ? "" : "Inbox"}</span>
              </Menu.Item>
              <Menu.Item className="sidebar_menuitem" key="ftc-reports" onClick={() => dispatch(push("/ftc-reports"))} title="Reports">
                <span style={{ fontSize: 15 }}>{collapsed ? "" : "Reports"}</span>
              </Menu.Item>
            </SubMenu>
          )}

          {/* Platform CRM + Predetermination Rules moved into Admin Dashboards (super-admin only) */}

          {/* My Office SubMenu */}
          <SubMenu
            key="sub5"
            className="sidebar_submenu"
            title={
              <span className="align-menu">
                <img src={office} alt="" className="Icon" />
                <span style={{ fontSize: 16 }}>{collapsed ? "" : "My Office"}</span>
              </span>
            }
          >
            <Menu.Item className="sidebar_menuitem" key="8" onClick={() => dispatch(push("/office/profile"))}>
              <span>Edit Profile</span>
            </Menu.Item>

            <Menu.Item className="sidebar_menuitem" key="16" onClick={() => dispatch(push("/office/providers"))}>
              <span>Providers</span>
            </Menu.Item>

            {isSuperAdmin && (
              <Menu.Item className="sidebar_menuitem" key="78" onClick={() => dispatch(push("/admin/insurance-carrier-calls"))}>
                <span>Insurance Call Config</span>
              </Menu.Item>
            )}

            <Menu.Item className="sidebar_menuitem" key="79" onClick={() => dispatch(push("/office/portal-credentials"))}>
              <span>Portal Credentials</span>
            </Menu.Item>

            <Menu.Item className="sidebar_menuitem" key="15" onClick={() => dispatch(push("/office/appointments"))}>
              <span>Appointment Types</span>
            </Menu.Item>

            {/* key was "70", which "Create Subscription" (/plans) in the
                Membership submenu also uses — a duplicate key in one antd Menu
                highlights BOTH items at once. */}
            <Menu.Item className="sidebar_menuitem" key="73" onClick={() => dispatch(push("/Services-management"))}>
              <span>Services</span>
            </Menu.Item>

            {isSuperAdmin && (
              <Menu.Item className="sidebar_menuitem" key="12" onClick={() => dispatch(push("/office/review"))}>
                <span>Check Reviews</span>
              </Menu.Item>
            )}

            <Menu.Item className="sidebar_menuitem" key="report-bug" onClick={openBugReporter} style={{ marginBottom: "0px" }}>
              <span><BugOutlined style={{ marginRight: 8 }} />Report a bug</span>
            </Menu.Item>
          </SubMenu>

          {/* Analytics Dashboard moved into Admin Dashboards (super-admin only) */}

          {/* Membership SubMenu */}
          {/* <SubMenu
            key="sub1"
            className="sidebar_submenu"
            title={
              <span className="align-menu">
                <img src={member} alt="" className="Icon" />
                <span style={{ fontSize: 16 }}>
                  {collapsed ? "" : "Membership"}
                </span>
              </span>
            }
          >
            <Menu.Item
              className="sidebar_menuitem"
              key="60"
              onClick={() => dispatch(push("/members"))}
            >
              <span>Members</span>
            </Menu.Item>
            <Menu.Item
              className="sidebar_menuitem"
              key="70"
              onClick={() => dispatch(push("/plans"))}
            >
              <span>Create Subscription</span>
            </Menu.Item>
            <Menu.Item
              className="sidebar_menuitem"
              key="71"
              onClick={() => dispatch(push("/Services-management"))}
            >
              <span>Services</span>
            </Menu.Item>
            <Menu.Item
              className="sidebar_menuitem"
              key="72"
              onClick={() => dispatch(push("/plans-management"))}
            >
              <span>Plans</span>
            </Menu.Item>
            <Menu.Item
              className="sidebar_menuitem"
              key="80"
              onClick={() => dispatch(push("/visit"))}
              style={{ marginBottom: "0px" }}
            >
              <span>Visits</span>
            </Menu.Item>
          </SubMenu> */}

          {/* Appointments moved into Admin Dashboards (super-admin only) */}

          {/* Data moved into Admin Dashboards (super-admin only) */}

          {/* Patient Engagement moved into Admin Dashboards (super-admin only) */}

          {access.automationEngine && (
            <Menu.Item className="sidebar_menuitem" key="10" onClick={() => goProduct("/patient-board")} title="Automation Engine">
              <img src={dashboard} alt="" className="Icon" />
              <span style={{ fontSize: 16 }}>{collapsed ? "" : "Automation Engine"}</span>
            </Menu.Item>
          )}

          {/* Admin Dashboards — super-admin-only consolidation of all admin tooling.
              Flat, non-collapsible ItemGroup sections keep the tree shallow. */}
          {isSuperAdmin && (
            <SubMenu
              key="adminDash"
              className="sidebar_submenu"
              title={
                <span className="align-menu">
                  <img src={dashboard} alt="" className="Icon" />
                  <span style={{ fontSize: 16 }}>{collapsed ? "" : "Admin Access"}</span>
                </span>
              }
            >
              <ItemGroup key="adminDash-crm" title={collapsed ? "" : "Platform CRM"}>
                <Menu.Item className="sidebar_menuitem" key="crm" onClick={() => dispatch(push("/crm"))}>
                  <span>Practices</span>
                </Menu.Item>
                <Menu.Item className="sidebar_menuitem" key="crm-analytics" onClick={() => dispatch(push("/crm-analytics"))}>
                  <span>Analytics</span>
                </Menu.Item>
                <Menu.Item className="sidebar_menuitem" key="ftc-users" onClick={() => dispatch(push("/ftc-users"))}>
                  <span>FTC Users</span>
                </Menu.Item>
                {/* Inside the isSuperAdmin-gated "Admin Access" submenu, so it
                    inherits the gate — matching the endpoints, which are
                    IsSuperUser. */}
                <Menu.Item className="sidebar_menuitem" key="carrier-knowledge" onClick={() => dispatch(push("/carrier-knowledge"))}>
                  <span>Carrier Knowledge</span>
                </Menu.Item>
                <Menu.Item className="sidebar_menuitem" key="crm-settings" onClick={() => dispatch(push("/crm-settings"))}>
                  <span>Settings</span>
                </Menu.Item>
              </ItemGroup>

              <ItemGroup key="adminDash-dashboards" title={collapsed ? "" : "Dashboards"}>
                <Menu.Item className="sidebar_menuitem" key="analytics" onClick={() => dispatch(push("/analytics"))}>
                  <span>Analytics Dashboard</span>
                </Menu.Item>
                <Menu.Item className="sidebar_menuitem" key="predet-rules" onClick={() => dispatch(push("/admin/predetermination-rules"))}>
                  <span>Predetermination Rules</span>
                </Menu.Item>
              </ItemGroup>

              <ItemGroup key="adminDash-appointments" title={collapsed ? "" : "Appointments"}>
                <Menu.Item className="sidebar_menuitem" key="6" onClick={() => dispatch(push("/appointments"))}>
                  <span>Appointments</span>
                </Menu.Item>
                <Menu.Item className="sidebar_menuitem" key="3" onClick={() => dispatch(push("/add-provider"))}>
                  <span>Add Provider</span>
                </Menu.Item>
                <Menu.Item className="sidebar_menuitem" key="5" onClick={() => dispatch(push("/provider-scheduling"))}>
                  <span>Provider Scheduling</span>
                </Menu.Item>
                <Menu.Item className="sidebar_menuitem" key="143" onClick={() => dispatch(push("/discount-options"))}>
                  <span>Discount Options</span>
                </Menu.Item>
              </ItemGroup>

              <ItemGroup key="adminDash-data" title={collapsed ? "" : "Data"}>
                <Menu.Item className="sidebar_menuitem" key="30" onClick={() => dispatch(push("/data/health-categories"))}>
                  <span>Health Categories</span>
                </Menu.Item>
                <Menu.Item className="sidebar_menuitem" key="31" onClick={() => dispatch(push("/data/procedures"))}>
                  <span>Procedures</span>
                </Menu.Item>
              </ItemGroup>

              <ItemGroup key="adminDash-engagement" title={collapsed ? "" : "Patient Engagement"}>
                <Menu.Item className="sidebar_menuitem" key="campaigns" onClick={() => dispatch(push("/campaigns"))}>
                  <span>Engagement Campaigns</span>
                </Menu.Item>
              </ItemGroup>

              <ItemGroup key="adminDash-beta" title={collapsed ? "" : "Beta Features"}>
                <Menu.Item className="sidebar_menuitem" key="144" onClick={() => dispatch(push("/insurance"))}>
                  <span>Predetermination and Post Payment</span>
                </Menu.Item>
                <Menu.Item className="sidebar_menuitem" key="145" onClick={() => dispatch(push("/insurance-breakdown"))}>
                  <span>Insurance Breakdowns</span>
                </Menu.Item>
                <Menu.Item className="sidebar_menuitem" key="0" onClick={() => dispatch(push("/dashboard"))}>
                  <span>Dashboard</span>
                </Menu.Item>
                <Menu.Item
                  className="sidebar_menuitem"
                  key="7"
                  style={{ marginBottom: "0px" }}
                  onClick={() => {
                    localStorage.setItem("patient-engagment-sub", "treatment-plans-account-receivable");
                    dispatch(push("/treatment-plans-account-receivable"));
                  }}
                >
                  <span>Accounts Receivable</span>
                </Menu.Item>
              </ItemGroup>
            </SubMenu>
          )}

          {/* Settings SubMenu */}
          {/* <SubMenu
            key="sub6"
            className="sidebar_submenu"
            title={
              <span className="align-menu">
                <img src={setting} alt="settings" className="Icon" />
                <span style={{ fontSize: 16 }}>{collapsed ? "" : "Settings"}</span>
              </span>
            }
          >
            <Menu.Item className="sidebar_menuitem" key="4" onClick={() => dispatch(push("/add-connected-account"))}>
              <span>Configure Banking</span>
            </Menu.Item>
            {isPracticeAdmin ? (
              <Menu.Item className="sidebar_menuitem" key="132" onClick={() => dispatch(push("/offices"))}>
                <span>User Management</span>
              </Menu.Item>
            ) : null}
          </SubMenu> */}

        </Menu>
        </div>

        <div className="sidebar-footer">
          <Menu mode="inline" theme="dark" className="collapsedMenu" selectable={false}>
            <Menu.Item className="sidebar_menuitem" key="sub6" style={{ marginBottom: "0px" }}>
              <Popover
                trigger="click"
                placement="right"
                content={
                  <Flex vertical align="start" gap={10}>
                    <Button style={{ border: "none", marginBottom: 0 }} onClick={() => dispatch(push("/add-connected-account"))}>
                      <span>Configure Banking</span>
                    </Button>
                    {access.userManagement ? (
                      <Button style={{ border: "none", marginBottom: 0 }} onClick={() => dispatch(push("/offices"))}>
                        <span>User Management</span>
                      </Button>
                    ) : null}
                    {access.billing ? (
                      <Button style={{ border: "none", marginBottom: 0 }} onClick={() => dispatch(push("/billing"))}>
                        <span>Billing</span>
                      </Button>
                    ) : null}
                  </Flex>
                }
              >
                <span className="align-menu">
                  <img src={setting} alt="settings" className="Icon" />
                  <span style={{ fontSize: 16 }}>{collapsed ? "" : "Settings"}</span>
                </span>
              </Popover>
            </Menu.Item>
          </Menu>
        </div>
      </Sider>
      <Modal
        open={displaySidebar !== "none"}
        onCancel={() => {
          setDisplaySidebar("none");
        }}
        className="sidebar_modal"
      ></Modal>
    </div>
  );
}

function mapStateToProps(state) {
  const { sendingLoginEmail, loginEmailSent, loggingIn, error } = state.authentication;
  return {
    sendingLoginEmail,
    loginEmailSent,
    loggingIn,
    error,
  };
}

const connectedSidebar = connect(mapStateToProps)(Sidebar);

export default withRouter(connectedSidebar);
